/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * DeepDive Component
 * 
 * Provides contextual deep-dive explanations that can be opened inline
 * without disrupting the main story flow. Deep dives are optional and
 * feel like "side notes" rather than a second journey.
 */

import { useState } from 'react';

export interface DeepDiveContent {
  id: string;
  title: string;
  content: React.ReactNode;
  preview?: string; // Part 5: Optional preview text
}

interface DeepDiveProps {
  trigger: string;
  content: DeepDiveContent;
}

function DeepDive({ trigger, content }: DeepDiveProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="deep-dive-container">
      <button
        className="deep-dive-trigger"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls={`deep-dive-${content.id}`}
      >
        {trigger}
      </button>
      {/* Part 5: Show preview if available */}
      {content.preview && !isOpen && (
        <span className="deep-dive-preview"> — {content.preview}</span>
      )}

      {isOpen && (
        <div
          id={`deep-dive-${content.id}`}
          className="deep-dive-panel"
          role="region"
          aria-label={content.title}
        >
          <div className="deep-dive-header">
            <h3 className="deep-dive-title">
              {/* Part 5: Add "Optional:" prefix */}
              <span className="optional-label">Optional:</span> {content.title}
            </h3>
            <button
              className="deep-dive-close"
              onClick={handleToggle}
              aria-label="Close deep dive"
            >
              ✕
            </button>
          </div>
          <div className="deep-dive-body">
            {content.content}
          </div>
        </div>
      )}
    </div>
  );
}

export default DeepDive;
